import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { clearAllQuizDrafts } from "../quiz/builderModel";
import type { UserPublic } from "../../services/api";
import { decodeTokenClaims, isTokenExpired } from "./tokenUtils";

interface UserProfile {
    userId: string | null;
    fullName: string | null;
    role: string | null;
    email: string | null;
    tenantSlug: string | null;
    coachingId: string | null;
    /** Server-stored interface language. The i18n provider adopts this on load
     *  so a student's choice follows them to another device, while the
     *  localStorage cache is what prevents a flash of English on first paint. */
    preferredLanguage: string | null;
    /** Someone else chose this account's password (a school admin, an
     *  import): the app shows only "choose your own password" until it is
     *  changed. The server refuses everything else meanwhile. */
    mustChangePassword: boolean;
}

interface AuthState {
    accessToken: string | null;
    refreshToken: string | null;
    profile: UserProfile | null;
}

const initialState: AuthState = {
    accessToken: null,
    refreshToken: null,
    profile: null
};

// Derives a lightweight profile snapshot from the JWT so we can bootstrap the UI even before hitting user APIs.
const deriveProfile = (token: string | null): UserProfile | null => {
    if (!token || isTokenExpired(token)) {
        return null;
    }
    try {
        const claims = decodeTokenClaims(token);
        const role = typeof claims.role === "string" ? claims.role : null;
        const fullName = typeof claims.full_name === "string" ? claims.full_name : null;
        const email = typeof claims.email === "string" ? claims.email : null;
        const tenantSlug = typeof claims.tenant_id === "string" ? claims.tenant_id : null;
        const userId = typeof claims.sub === "string" ? claims.sub : null;
        const coachingId =
            typeof claims.coaching_id === "string"
                ? claims.coaching_id
                : typeof claims.coachingId === "string"
                ? (claims.coachingId as string)
                : null;
        if (!role && !fullName && !email && !tenantSlug && !coachingId && !userId) {
            return null;
        }
        return {
            userId,
            role,
            fullName,
            email,
            tenantSlug,
            coachingId,
            // The JWT carries no language claim; the login response and the
            // profile screen supply it. Null here means "not known yet", and the
            // localStorage cache covers first paint.
            preferredLanguage: null,
            mustChangePassword: claims.pwd_change === "1"
        };
    }
    catch (error) {
        console.warn("Unable to decode token claims while deriving profile", error);
        return null;
    }
};

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        setTokens(state: AuthState, action: PayloadAction<{ access: string; refresh: string; user?: UserPublic }>) {
            state.accessToken = action.payload.access;
            state.refreshToken = action.payload.refresh;
            if (action.payload.user) {
                state.profile = {
                    userId: action.payload.user.id,
                    role: action.payload.user.role,
                    fullName: action.payload.user.full_name,
                    email: action.payload.user.email,
                    preferredLanguage: action.payload.user.preferred_language ?? null,
                    tenantSlug: action.payload.user.tenant_slug ?? null,
                    coachingId: action.payload.user.coaching_id ?? null,
                    mustChangePassword: Boolean(action.payload.user.must_change_password)
                };
            } else {
                state.profile = deriveProfile(action.payload.access);
            }
            localStorage.setItem("accessToken", action.payload.access);
            localStorage.setItem("refreshToken", action.payload.refresh);
        },
        hydrateFromStorage(state: AuthState) {
            const accessToken = localStorage.getItem("accessToken");
            const refreshToken = localStorage.getItem("refreshToken");
            if (!accessToken) {
                state.accessToken = null;
                state.refreshToken = null;
                state.profile = null;
                return;
            }
            if (isTokenExpired(accessToken)) {
                localStorage.removeItem("accessToken");
                localStorage.removeItem("refreshToken");
                state.accessToken = null;
                state.refreshToken = null;
                state.profile = null;
                return;
            }
            state.accessToken = accessToken;
            state.refreshToken = refreshToken;
            state.profile = deriveProfile(accessToken);
        },
        /** A self-edit (name) saved on the server; the token catches up on refresh. */
        updateProfile(state: AuthState, action: PayloadAction<{ fullName?: string }>) {
            if (state.profile && action.payload.fullName) {
                state.profile.fullName = action.payload.fullName;
            }
        },
        signOut(state: AuthState) {
            state.accessToken = null;
            state.refreshToken = null;
            state.profile = null;
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            // THE TOKENS WERE NOT THE ONLY THING OF THEIRS ON THIS MACHINE.
            //
            // An in-progress quiz draft outlived logout, and its key carried no
            // owner, so the next account to open the builder was offered the
            // previous student's paper -- questions, and the answer key with
            // them. The keys are namespaced now, which stops it being READ;
            // this stops it being LEFT, which is the point of logging out on a
            // shared computer.
            clearAllQuizDrafts();
        }
    }
});

export const { setTokens, hydrateFromStorage, signOut, updateProfile } = authSlice.actions;
export default authSlice.reducer;
